/**
 * 진단 테스트 세트 문항 미리보기 패널.
 * apps/admin/src/components/inspection/QuizPreviewPanel.tsx 포팅 (UI 문자열만 i18n 적용).
 */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { Loader2, RotateCcw, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  QuizRenderer,
  MultiChoiceQuizForDiary,
  FillBlankQuizForDiary,
  SelectTextQuizForDiary,
  extractPlayableSolutionVideoUrl,
  isNewUiQuiz,
  substituteVariables,
} from '@quebon/shared-learning'
import type {
  QuizScript,
  QuizSolveRequest,
  SolveResult,
} from '@quebon/shared-learning'
import type {
  QuizData,
  QuizSolveResult,
  MultiChoiceQuizData,
  FillBlankQuizData,
  SelectTextQuizData,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'

interface Props {
  quizId: string
  /** 특강 미리보기에서 해설 URL 문자열을 숨기고 인라인 영상으로 표시한다. */
  showExplanationVideo?: boolean
}

const UnitAssessmentExplanationVideo = lazy(() =>
  import('@/components/admin/content/UnitAssessmentExplanationVideo').then(module => ({
    default: module.UnitAssessmentExplanationVideo,
  })),
)

function toLegacyQuiz(q: QuizScript | null): QuizData | null {
  if (!q) return null
  const raw = q as unknown as Record<string, unknown>
  const qd = (raw.quizData as QuizData) || (q as unknown as QuizData)
  return {
    ...qd,
    type: q.type || qd.type,
    title: q.title || qd.title,
    problem: q.problem || qd.problem,
    vars: q.vars || qd.vars,
    image: q.image || qd.image,
    imageMobile: q.imageMobile || qd.imageMobile,
  } as QuizData
}

function buildRevealResult(q: QuizScript): QuizSolveResult {
  const desc = q.description
  // 해설 텍스트의 #{var} placeholder를 concrete vars(getVarsWithSampleValueMap)로 치환.
  // problem/answers는 서버에서 이미 치환되지만 description은 원본으로 내려온다.
  const vars = q.vars
  const substitutedText =
    desc?.text && vars && Object.keys(vars).length > 0
      ? substituteVariables(desc.text, vars as Record<string, unknown>)
      : desc?.text
  return {
    correct: true,
    corrects: [],
    correctAnswers: (q.correctAnswers ?? []).map(a => String(a)),
    elapsedTime: 0,
    description: desc
      ? {
          text: substitutedText ?? undefined,
          image: (() => {
            const img = (desc as Record<string, unknown>).image
            if (typeof img === 'string') return { signedUrl: img }
            if (img && typeof img === 'object') return img as { signedUrl: string }
            return undefined
          })(),
          imageMobile: (() => {
            const img = (desc as Record<string, unknown>).imageMobile
            if (typeof img === 'string') return { signedUrl: img }
            if (img && typeof img === 'object') return img as { signedUrl: string }
            return undefined
          })(),
          desmos: desc.desmos ?? undefined,
        }
      : undefined,
  }
}

function transformAnswers(
  type: QuizData['type'],
  answers: string[],
  legacy: QuizData
): (string | number | boolean)[] {
  switch (type) {
    case 'fill_blank':
      return answers
    case 'multi_choice': {
      const quiz = legacy as MultiChoiceQuizData
      return answers
        .map(key => {
          const idx = quiz.choices.findIndex(c => c.key === key)
          return idx >= 0 ? idx + 1 : 0
        })
        .filter(idx => idx > 0)
    }
    case 'select_text':
      return answers.map(a => {
        const n = parseInt(a, 10)
        return isNaN(n) ? 0 : n
      })
    case 'boolean':
      return answers.map(a => a === 'true')
    case 'drag_text':
    case 'coloring':
    case 'counting':
    case 'image_select':
    case 'ordering':
    case 'pairing':
      return answers
    default:
      return []
  }
}

export function QuizPreviewPanel({ quizId, showExplanationVideo = false }: Props) {
  const { t } = useTranslation('admin')
  const [quizData, setQuizData] = useState<QuizScript | null>(null)
  const [solveResult, setSolveResult] = useState<QuizSolveResult | null>(null)
  const [submittedAnswers, setSubmittedAnswers] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [solving, setSolving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetKey, setResetKey] = useState(0)
  const [revealAnswers, setRevealAnswers] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setQuizData(null)
    setSolveResult(null)
    setSubmittedAnswers([])

    quizScriptApi
      .getQuiz(quizId, false, undefined, true, revealAnswers)
      .then(data => {
        if (cancelled) return
        setQuizData(data)
        if (revealAnswers) setSolveResult(buildRevealResult(data))
      })
      .catch(err => {
        console.error('getQuiz failed', err)
        if (!cancelled) setError(t('diagnosticSets.quizPreview.loadFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [quizId, resetKey, revealAnswers, t])

  const handleSolve = useCallback(
    async (
      _quizId: string | number,
      answers: string[],
      quizVars: Record<string, unknown>,
      elapsedTime: number,
    ) => {
      if (revealAnswers) return
      if (!quizData || solving) return
      const legacy = toLegacyQuiz(quizData)
      if (!legacy) return

      setSubmittedAnswers(answers)
      setSolving(true)

      try {
        const request: QuizSolveRequest = {
          answers: transformAnswers(legacy.type, answers, legacy),
          vars: quizVars,
          elapsed: elapsedTime,
        }
        const apiResult: SolveResult = await quizScriptApi.solve(
          String(quizData.quizId ?? quizId),
          request,
        )
        const result: QuizSolveResult = {
          correct: apiResult.correct,
          corrects: apiResult.corrects.map(c => {
            const v = c as unknown
            return Boolean(v === true || v === 'true')
          }),
          correctAnswers: (apiResult.correctAnswers ?? []).map(a => String(a)),
          elapsedTime,
          description: apiResult.description
            ? {
                text: apiResult.description.text ?? undefined,
                image: (() => {
                  const img = (apiResult.description as Record<string, unknown>).image
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                imageMobile: (() => {
                  const img = (apiResult.description as Record<string, unknown>).imageMobile
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                desmos: apiResult.description.desmos ?? undefined,
              }
            : undefined,
        }
        setSolveResult(result)
      } catch (err) {
        console.error('solve failed', err)
        setSolveResult({
          correct: false,
          corrects: [],
          correctAnswers: [],
          elapsedTime,
        })
      } finally {
        setSolving(false)
      }
    },
    [quizData, solving, quizId, revealAnswers],
  )

  const handleReset = useCallback(() => {
    setResetKey(k => k + 1)
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <AlertCircle className="w-8 h-8 text-rose-400" />
        <p className="text-sm text-rose-600">{error}</p>
        <button
          onClick={handleReset}
          className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          {t('diagnosticSets.quizPreview.retry')}
        </button>
      </div>
    )
  }

  if (!quizData) return null

  const legacy = toLegacyQuiz(quizData)
  if (!legacy) return null

  const currentId = String(quizData.quizId ?? quizId)
  const newUi = isNewUiQuiz(currentId)
  const renderKey = `${currentId}-${resetKey}-${revealAnswers ? 'reveal' : 'solve'}-${solveResult ? 'solved' : 'unsolved'}`
  const explanationVideoUrl = showExplanationVideo
    ? extractPlayableSolutionVideoUrl(solveResult?.description?.text)
    : null
  const rendererSolveResult = explanationVideoUrl && solveResult
    ? { ...solveResult, description: undefined }
    : solveResult

  return (
    <div className="relative">
      <button
        onClick={handleReset}
        className="absolute top-0 right-0 z-10 flex items-center gap-1 px-2 py-1 text-xs text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
        title={t('diagnosticSets.quizPreview.reset')}
      >
        <RotateCcw className="w-3 h-3" />
        {t('diagnosticSets.quizPreview.reset')}
      </button>
      <button
        onClick={() => setRevealAnswers(v => !v)}
        className="absolute top-0 right-16 z-10 flex items-center gap-1 px-2 py-1 text-xs text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
        title={t('diagnosticSets.quizPreview.revealTooltip')}
      >
        {revealAnswers
          ? t('diagnosticSets.quizPreview.hideAnswers')
          : t('diagnosticSets.quizPreview.showAnswers')}
      </button>

      {newUi ? (
        <div className="quiz-new-ui min-h-[300px] rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4">
          {legacy.type === 'fill_blank' && (
            <FillBlankQuizForDiary
              key={renderKey}
              quizId={currentId}
              quiz={legacy as FillBlankQuizData}
              onSolve={handleSolve}
              solveResult={rendererSolveResult}
              showReallyTrue={revealAnswers}
              isViewSolved={revealAnswers}
              initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
            />
          )}
          {legacy.type === 'multi_choice' && (
            <MultiChoiceQuizForDiary
              key={renderKey}
              quizId={currentId}
              quiz={legacy as MultiChoiceQuizData}
              onSolve={handleSolve}
              solveResult={rendererSolveResult}
              showReallyTrue={revealAnswers}
              isViewSolved={revealAnswers}
              initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
            />
          )}
          {legacy.type === 'select_text' && (
            <SelectTextQuizForDiary
              key={renderKey}
              quizId={currentId}
              quiz={legacy as SelectTextQuizData}
              onSolve={handleSolve}
              solveResult={rendererSolveResult}
              showReallyTrue={revealAnswers}
              isViewSolved={revealAnswers}
              initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
            />
          )}
        </div>
      ) : (
        <div className="min-h-[300px]">
          <QuizRenderer
            key={renderKey}
            quizType={legacy.type}
            quizId={currentId}
            quiz={legacy}
            onSolve={handleSolve}
            solveResult={rendererSolveResult}
            showReallyTrue={revealAnswers}
            isViewSolved={revealAnswers}
            useKeypad={false}
            hideExplanation={explanationVideoUrl != null}
            initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
          />
        </div>
      )}

      {explanationVideoUrl ? (
        <Suspense fallback={null}>
          <UnitAssessmentExplanationVideo videoUrl={explanationVideoUrl} />
        </Suspense>
      ) : null}

      {solving && (
        <div className="flex justify-center items-center gap-2 mt-4 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t('diagnosticSets.quizPreview.grading')}</span>
        </div>
      )}
    </div>
  )
}
